import { COMPANY_NAME, LEGAL_LINKS, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, MAIN_CONTENT_ID, SkipLink, useTheme } from "@sagot/ui";
import { useEffect, type ReactNode } from "react";
import "../auth.css";
import { AuthAside } from "./auth-aside";
import { AuthLogo } from "./auth-logo";
import { AuthThemeSwitch } from "./theme-switch";

export interface AuthHeaderPrompt {
  /** Muted text before the link (hidden < sm), e.g. "New to Sagot?". */
  text: string;
  label: string;
  href: string;
}

export interface AuthHeaderBack {
  label: string;
  href: string;
}

interface AuthLayoutProps {
  /** Document title (`<title>`). */
  title: string;
  /** Right side: prompt + outlined link (login, forgot, reset, register, invite). */
  prompt?: AuthHeaderPrompt;
  /** Left side, after the logo: back link (verify, 2FA). */
  back?: AuthHeaderBack;
  /** Card width: `sm` for short forms, `md` for the long ones (register, invite). */
  width?: "sm" | "md";
  /** `start` keeps tall forms (register, invite) top-aligned on small screens. */
  align?: "center" | "start";
  homeHref?: string;
  /** The three-point trust line under the card (default on). */
  trust?: boolean;
  children: ReactNode;
}

const TRUST_POINTS = [
  { key: "auth.aside.citeTitle", tone: "blue" },
  { key: "auth.aside.channelsTitle", tone: "sun" },
  { key: "auth.aside.privacyTitle", tone: "green" },
] as const;

function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function Legal() {
  const { t } = useTranslation();
  const newTab = t("auth.common.opensNewTab");
  return (
    <footer className="a-legal">
      <span>{t("auth.aside.footer", { company: COMPANY_NAME })}</span>
      <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
        {t("auth.common.terms")}
        <span className="sr-only"> {newTab}</span>
      </a>
      <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
        {t("auth.common.privacy")}
        <span className="sr-only"> {newTab}</span>
      </a>
      <a href={LEGAL_LINKS.status} target="_blank" rel="noopener noreferrer">
        {t("auth.common.status")}
        <span className="sr-only"> {newTab}</span>
      </a>
    </footer>
  );
}

/**
 * Auth shell of the redesign (mockups/marketing, WP-RD-9): sand page, floating tiles and a sample widget card (>= 900 px), a white card
 * with the serif title, trust line and legal footer. The marketing tokens apply under `.auth-shell` only; `data-theme` follows the
 * console theme, so the shell has a light and a dark variant. Shared by the console auth routes and the admin sign-in.
 */
export function AuthLayout({ title, prompt, back, width = "sm", align = "center", homeHref = "/", trust = true, children }: AuthLayoutProps) {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  useDocumentTitle(title);
  return (
    <div className="auth-shell a-page" data-theme={resolved}>
      <SkipLink label={t("shell.skipToContent")} />
      <AuthAside />
      <header className="a-top">
        <AppLink href={homeHref} className="a-logo-link" aria-label={t("auth.aside.homeLabel", { product: PRODUCT_NAME })}>
          <AuthLogo />
        </AppLink>
        {back ? (
          <AppLink href={back.href} className="a-top-link a-back">
            <Icon name="arrow-left" />
            {back.label}
          </AppLink>
        ) : null}
        <div className="a-top-end">
          {prompt ? (
            <>
              <span className="a-top-text">{prompt.text}</span>
              <AppLink href={prompt.href} className="a-top-link">
                {prompt.label}
              </AppLink>
            </>
          ) : null}
          <AuthThemeSwitch />
        </div>
      </header>
      <main id={MAIN_CONTENT_ID} tabIndex={-1} className="a-center" data-align={align}>
        <div className="a-stack">
          <div className="a-card" data-width={width}>
            {children}
          </div>
          {trust ? (
            <ul className="a-trust">
              {TRUST_POINTS.map((point) => (
                <li key={point.key}>
                  <i data-tone={point.tone} aria-hidden="true" />
                  {t(point.key)}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </main>
      <Legal />
    </div>
  );
}
